import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";
import { getAIExhibitBatch3Copy } from "../content/aiExhibitBatch3Content";

const sigmoid = (z) => 1 / (1 + Math.exp(-z));

function levelValue(level) {
  if (level === "low") return 0.15;
  if (level === "high") return 0.9;
  return 0.5;
}

function ActivationNode({ label, value, emphasized = false }) {
  const pct = Math.round(value * 100);

  return (
    <View
      style={{
        width: emphasized ? 150 : 132,
        minHeight: emphasized ? 150 : 132,
        borderRadius: 999,
        borderWidth: emphasized ? 3 : 2,
        borderColor: emphasized ? C.success : C.accent,
        backgroundColor: C.surfaceRaised,
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
      }}
    >
      <Text
        style={{
          color: emphasized ? C.success : C.accent,
          fontSize: emphasized ? 31 : 26,
          fontWeight: "900",
        }}
      >
        {pct}%
      </Text>
      <Text
        style={{
          color: C.text,
          fontSize: 12,
          lineHeight: 17,
          fontWeight: "800",
          textAlign: "center",
          marginTop: 6,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function LevelButtons({ value, onChange, labels }) {
  return (
    <View style={{ flexDirection: "row", gap: 8, marginTop: 10 }}>
      {[
        ["low", labels.low],
        ["medium", labels.medium],
        ["high", labels.high],
      ].map(([id, label]) => {
        const active = value === id;
        return (
          <Pressable
            key={id}
            onPress={() => onChange(id)}
            style={({ pressed }) => ({
              flex: 1,
              minHeight: 52,
              borderRadius: 13,
              borderWidth: active ? 2 : 1,
              borderColor: active ? C.accent : C.border,
              backgroundColor: active || pressed ? C.accentSoft : C.surface,
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 10,
            })}
          >
            <Text style={{ color: C.text, fontSize: 15, fontWeight: "900" }}>
              {label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export default function NeuralNetworkScreen({
  navigate,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t = getAIExhibitBatch3Copy(language).neuralNetwork;
  const [blue, setBlue] = useState("medium");
  const [round, setRound] = useState("medium");

  const values = useMemo(() => {
    const x1 = levelValue(blue);
    const x2 = levelValue(round);

    const h1 = sigmoid(5 * x1 - 2.5);
    const h2 = sigmoid(5 * x2 - 2.5);
    const h3 = sigmoid(4 * (x1 + x2 - 1));
    const output = sigmoid(3 * h1 + 3 * h2 + 2 * h3 - 4);

    return { x1, x2, h1, h2, h3, output };
  }, [blue, round]);

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>
        {t.eyebrow}
      </Text>
      <Text style={{ color: C.text, fontSize: 44, lineHeight: 50, fontWeight: "900", marginTop: 8 }}>
        {t.title}
      </Text>
      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("neural_network_overview")}
        style={{ marginTop: 8, maxWidth: 980, paddingRight: 34 }}
      >
        <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27 }}>
          {t.subtitle}
        </Text>
      </AIExhibitNarrationPressable>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 18, marginTop: 24 }}>
        <View style={{ width: 330, maxWidth: "100%", padding: 20, borderRadius: 18, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          <Text style={{ color: C.text, fontSize: 19, fontWeight: "900" }}>{t.inputs}</Text>

          <Text style={{ color: C.muted, fontSize: 15, fontWeight: "800", marginTop: 18 }}>{t.featureBlue}</Text>
          <LevelButtons value={blue} onChange={setBlue} labels={t} />

          <Text style={{ color: C.muted, fontSize: 15, fontWeight: "800", marginTop: 20 }}>{t.featureRound}</Text>
          <LevelButtons value={round} onChange={setRound} labels={t} />

          <View style={{ marginTop: 22, padding: 15, borderRadius: 14, backgroundColor: C.surface }}>
            <Text style={{ color: C.text, fontSize: 15, lineHeight: 23 }}>
              {language === "es"
                ? `Entradas numéricas: azul ${Math.round(values.x1 * 100)}%, redondez ${Math.round(values.x2 * 100)}%.`
                : `Numerical inputs: blue ${Math.round(values.x1 * 100)}%, round ${Math.round(values.x2 * 100)}%.`}
            </Text>
          </View>
        </View>

        <View style={{ flex: 1, minWidth: 500, padding: 20, borderRadius: 18, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-around", gap: 18, flexWrap: "wrap" }}>
            <View style={{ alignItems: "center", gap: 10 }}>
              <Text style={{ color: C.muted, fontSize: 13, fontWeight: "900" }}>{t.inputs.toUpperCase()}</Text>
              <ActivationNode label={t.featureBlue} value={values.x1} />
              <ActivationNode label={t.featureRound} value={values.x2} />
            </View>

            <Text style={{ color: C.accent, fontSize: 34, fontWeight: "900" }}>→</Text>

            <View style={{ alignItems: "center", gap: 10 }}>
              <Text style={{ color: C.muted, fontSize: 13, fontWeight: "900" }}>{t.hidden.toUpperCase()}</Text>
              <ActivationNode label={t.node1} value={values.h1} />
              <ActivationNode label={t.node2} value={values.h2} />
              <ActivationNode label={t.node3} value={values.h3} />
            </View>

            <Text style={{ color: C.accent, fontSize: 34, fontWeight: "900" }}>→</Text>

            <View style={{ alignItems: "center", gap: 10 }}>
              <Text style={{ color: C.muted, fontSize: 13, fontWeight: "900" }}>{t.output.toUpperCase()}</Text>
              <ActivationNode label={t.result} value={values.output} emphasized />
            </View>
          </View>
        </View>
      </View>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("neural_network_what_is_happening")}
        style={{ marginTop: 18, padding: 18, borderRadius: 16, borderLeftWidth: 5, borderWidth: 1, borderColor: C.border, borderLeftColor: C.accent, backgroundColor: C.accentSoft }}
      >
        <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>{t.takeawayTitle}</Text>
        <Text style={{ color: C.text, fontSize: 16, lineHeight: 25, marginTop: 8 }}>{t.takeaway}</Text>
      </AIExhibitNarrationPressable>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 14, marginTop: 14 }}>
        <AIExhibitNarrationPressable
          audioEnabled={audioEnabled}
          onPress={() => playNarration("neural_network_scale_caveat")}
          style={{ flex: 1, minWidth: 300, padding: 16, borderRadius: 15, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}
        >
          <Text style={{ color: C.muted, fontSize: 14, lineHeight: 22 }}>{t.caveat}</Text>
        </AIExhibitNarrationPressable>
        <AIExhibitNarrationPressable
          audioEnabled={audioEnabled}
          onPress={() => playNarration("neural_network_not_tiny_brain")}
          style={{ flex: 1, minWidth: 300, padding: 16, borderRadius: 15, borderWidth: 1, borderColor: C.warning, backgroundColor: C.surfaceRaised }}
        >
          <Text style={{ color: C.text, fontSize: 14, lineHeight: 22 }}>{t.noBrain}</Text>
        </AIExhibitNarrationPressable>
      </View>

      <Pressable
        onPress={() => navigate("next_token")}
        style={({ pressed }) => ({
          marginTop: 20,
          alignSelf: "flex-start",
          minHeight: 58,
          paddingHorizontal: 22,
          paddingVertical: 14,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: C.accent,
          backgroundColor: pressed ? C.surfaceRaised : C.accentSoft,
        })}
      >
        <Text style={{ color: C.text, fontSize: 17, fontWeight: "900" }}>{t.next} →</Text>
      </Pressable>
    </View>
  );
}
